import { useState } from 'react';
import ATable, { ATableHeader } from '../../organisms/ATable';
import Dialog from '../../organisms/Dialog';
import BadgeStatus from '../../atoms/BadgeStatus';
import PageHeader from '../../molecules/PageHeader';
import { products, Product, LEGAL_INSTRUMENT_OPTIONS, getGuaranteeLabel } from './mockData';

const tableHeaders: ATableHeader[] = [
    { id: 'col-name', title: 'Produto', field: 'name', canSort: true, width: '320px' },
    { id: 'col-modality', title: 'Modalidade', field: 'modalityFormatted', canSort: true, width: '220px' },
    { id: 'col-instrument', title: 'Instrumento Jurídico', field: 'legalInstrument', canSort: true, width: '180px' },
    { id: 'col-rate', title: 'Taxa (% a.m.)', field: 'minRate', canSort: false, width: '130px' },
    { id: 'col-max-amount', title: 'Valor Máximo', field: 'maxAmount', canSort: true, width: '150px' },
    { id: 'col-max-term', title: 'Prazo Máximo', field: 'maxTerm', canSort: true, width: '120px' },
    { id: 'col-guarantees', title: 'Garantias', field: 'guarantees', canSort: false, width: '100px' },
];

const formatCurrency = (value: number, currency: 'BRL' | 'USD') =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency }).format(value);

const getLegalInstrumentLabel = (value: string) =>
    LEGAL_INSTRUMENT_OPTIONS.find(o => o.value === value)?.label || value;

const ProductPageList = () => {
    const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);

    return (
        <div className="p-6">
            <PageHeader
                title="Produtos"
                breadcrumb={[{ label: 'Contratos', url: '#' }, { label: 'Originação', url: '#' }, { label: 'Produtos', url: '#' }]}
                apiVersion=""
            >
                <></>
            </PageHeader>

            <div className="mt-4 bg-white dark:bg-slate-800 rounded-lg shadow">
                <ATable headers={tableHeaders}>
                    {products.map((product, index) => (
                        <tr
                            key={product.id}
                            onClick={() => setSelectedProduct(product)}
                            className={`${index % 2 === 0 ? 'bg-slate-100 dark:bg-slate-800' : 'dark:bg-slate-900'} hover:bg-slate-300 dark:hover:bg-slate-700 hover:shadow-md border-b border-gray-300 cursor-pointer`}
                        >
                            <td className="py-2 px-2 font-medium">{product.name}</td>
                            <td className="py-2 px-2">{product.modalityFormatted}</td>
                            <td className="py-2 px-2">{getLegalInstrumentLabel(product.legalInstrument)}</td>
                            <td className="py-2 px-2">{product.minRate}% — {product.maxRate}%</td>
                            <td className="py-2 px-2">{formatCurrency(product.maxAmount, product.currency)}</td>
                            <td className="py-2 px-2">{product.maxTerm} dias</td>
                            <td className="py-2 px-2 text-center">
                                <BadgeStatus color={product.guarantees.length > 0 ? 'green' : 'gray'} text={`${product.guarantees.length}`} />
                            </td>
                        </tr>
                    ))}
                </ATable>
            </div>

            <Dialog
                id="product-detail-modal"
                show={!!selectedProduct}
                onClose={() => setSelectedProduct(null)}
                header={`Produto: ${selectedProduct?.name || ''}`}
                size="lg"
                body={selectedProduct ? (
                    <div className="p-4 space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="text-xs text-gray-500 uppercase">Nome</label>
                                <p className="font-medium">{selectedProduct.name}</p>
                            </div>
                            <div>
                                <label className="text-xs text-gray-500 uppercase">Modalidade</label>
                                <p>{selectedProduct.modalityFormatted}</p>
                            </div>
                            <div>
                                <label className="text-xs text-gray-500 uppercase">Instrumento Jurídico</label>
                                <p>{getLegalInstrumentLabel(selectedProduct.legalInstrument)}</p>
                            </div>
                            <div>
                                <label className="text-xs text-gray-500 uppercase">Moeda</label>
                                <p>{selectedProduct.currency === 'BRL' ? 'Real (R$)' : 'Dólar (U$)'}</p>
                            </div>
                            <div>
                                <label className="text-xs text-gray-500 uppercase">Taxa Mínima</label>
                                <p>{selectedProduct.minRate}% a.m.</p>
                            </div>
                            <div>
                                <label className="text-xs text-gray-500 uppercase">Taxa Máxima</label>
                                <p>{selectedProduct.maxRate}% a.m.</p>
                            </div>
                            <div>
                                <label className="text-xs text-gray-500 uppercase">Valor Máximo</label>
                                <p>{formatCurrency(selectedProduct.maxAmount, selectedProduct.currency)}</p>
                            </div>
                            <div>
                                <label className="text-xs text-gray-500 uppercase">Prazo Máximo</label>
                                <p>{selectedProduct.maxTerm} dias</p>
                            </div>
                        </div>

                        <div className="border-t pt-4">
                            <label className="text-xs text-gray-500 uppercase">Garantias</label>
                            {selectedProduct.guarantees.length === 0 ? (
                                <p className="text-gray-400 italic">Nenhuma garantia configurada</p>
                            ) : (
                                <div className="mt-2 space-y-1">
                                    {selectedProduct.guarantees.map(g => (
                                        <div key={g.type} className="flex items-center space-x-2">
                                            <BadgeStatus
                                                color={g.requirement === 'MANDATORY' ? 'red' : 'blue'}
                                                text={g.requirement === 'MANDATORY' ? 'Obrigatória' : 'Opcional'}
                                            />
                                            <span className="text-sm">{getGuaranteeLabel(g.type)}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="border-t pt-4">
                            <label className="text-xs text-gray-500 uppercase">Descrição</label>
                            <p>{selectedProduct.description || '—'}</p>
                        </div>
                    </div>
                ) : <></>}
            />
        </div>
    );
};

export default ProductPageList;
